import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useSite } from "@/context/SiteContent";
import { Tilt } from "./Tilt";

const ease = [0.2, 0.7, 0.2, 1];

export const FivePs = ({ activeP, setActiveP }) => {
  const { fivePs } = useSite();
  const pData = fivePs.items;
  const P_ORDER = fivePs.order;
  const d = pData[activeP];
  const idx = P_ORDER.indexOf(activeP);
  const section = useRef(null);
  const [inView, setInView] = useState(false);
  const go = (dir) => setActiveP(P_ORDER[(idx + dir + P_ORDER.length) % P_ORDER.length]);

  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.3 });
    io.observe(section.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return undefined;
    const onKey = (e) => {
      if (/input|textarea|select/i.test(e.target.tagName)) return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <section ref={section} className="ps-section" data-rail="The 5Ps" id="fiveps" data-testid="fiveps-section">
      <div className="ps-spot" />
      <div className="container ps-wrap">
        <div className="ps-intro reveal">
          <div className="eyebrow">{fivePs.eyebrow}</div>
          <h2 style={{ color: "white", marginTop: 10 }}>{fivePs.headingTop}<br />{fivePs.headingBottom}</h2>
          <p className="lead">{fivePs.lead}</p>
        </div>
        <div className="ps-cards reveal">
          {P_ORDER.map((k) => (
            <Tilt as="button" max={10} key={k} className={`ps-card${activeP === k ? " active" : ""}`} onClick={() => setActiveP(k)} data-testid={`ps-card-${k}`}>
              <span className="pnum">{pData[k].i}</span><h3>{pData[k].name}</h3><p>{k === "profit" ? "What resources support the first four?" : pData[k].q}</p>
            </Tilt>
          ))}
        </div>
        <div className="ps-detail reveal" id="pDetail" data-testid="ps-detail">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div key={activeP} className="ps-detail-inner" initial={{ opacity: 0, x: 26 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -26 }} transition={{ duration: 0.34, ease }}>
              <div className="ps-detail-num">{d.i}</div>
              <div>
                <div className="ps-question">{d.q}</div>
                <h3 data-testid="ps-detail-title">{d.name}</h3>
                <p>{d.text}</p>
                <div className="tags">{d.tags.map((t, i) => <motion.span key={t} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 + i * 0.05 }}>{t}</motion.span>)}</div>
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="ps-stepper">
            <button type="button" onClick={() => go(-1)} aria-label="Previous P" data-testid="ps-prev-btn"><ArrowLeft size={16} /></button>
            <div className="ps-stepper-dots">
              {P_ORDER.map((k) => <button type="button" key={k} className={k === activeP ? "on" : ""} onClick={() => setActiveP(k)} aria-label={pData[k].name} />)}
            </div>
            <span className="ps-stepper-count" data-testid="ps-stepper-count">{d.i} / 05</span>
            <button type="button" onClick={() => go(1)} aria-label="Next P" data-testid="ps-next-btn"><ArrowRight size={16} /></button>
            <span className="ps-stepper-hint">Use ← → keys</span>
          </div>
        </div>
      </div>
    </section>
  );
};
